import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { UserService, LicenseType, RankType } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';
import { RegistrationConfirmModalComponent } from './registration-confirm-modal.component';
import provinceData from '../../../environments/province_with_district_and_sub_district.json';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss'
})
export class RegisterComponent implements OnInit, OnDestroy {
  createUserForm!: FormGroup;
  currentStep = 1;
  totalSteps = 2;
  isSubmitting = false;
  licenses: LicenseType[] = [];
  ranks: RankType[] = [];
  isLoadingData = false;
  userId: string = '';
  userCid: string = '';
  userEmail: string = '';
  courseList: any[] = [];
  isLoadingCourses = false;
  companyList: any[] = [];
  filteredCompanyList: any[] = [];
  isLoadingCompanies = false;
  selectedCompany: any = null;
  isUploadingProfile = false;
  companySearchText: string = '';
  showCompanyDropdown: boolean = false;
  birthDateBE: string = '';
  maxBirthDate: string = '';
  vatMemberExpiryBE: string = '';
  valuerRankExpiryBE: string = '';

  // Province data for cascading dropdowns
  provinceList: any[] = provinceData;
  
  // Registered address dropdowns
  registeredDistrictList: any[] = [];
  registeredSubdistrictList: any[] = [];
  registeredZipCodeList: any[] = [];
  
  // Current address dropdowns
  currentDistrictList: any[] = [];
  currentSubdistrictList: any[] = [];
  currentZipCodeList: any[] = [];

  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private userService: UserService,
    private authService: AuthService,
    private http: HttpClient,
    private modalService: NgbModal
  ) {
    this.initForm();
    this.loadUserFromLocalStorage();
    this.setMaxBirthDate();
  }

  ngOnInit(): void {
    this.setupAddressWatchers();
    this.initializeDynamicArrays();
    this.prefillUserFields();
    this.loadReferenceUserData();
    this.loadCourses();
    this.loadCompanies();
  }

  /**
   * Load user information from localStorage
   */
  private loadUserFromLocalStorage(): void {
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      this.userId = currentUser.id;
      this.userCid = currentUser.cid || '';
      this.userEmail = currentUser.email || '';
      console.log('📋 Loaded user from localStorage:', { userId: this.userId, cid: this.userCid, email: this.userEmail });
    } else {
      console.warn('⚠️ No user found in localStorage');
      // Redirect to login if no user found
      this.router.navigate(['/login']);
    }
  }

  /**
   * Prefill CID and Email fields with user's data from localStorage
   */
  private prefillUserFields(): void {
    // Prefill CID
    if (this.userCid) {
      this.createUserForm.patchValue({ cid: this.userCid });
      // Make CID field readonly
      this.createUserForm.get('cid')?.disable();
    }

    // Prefill Email
    if (this.userEmail) {
      this.createUserForm.patchValue({ email: this.userEmail });
      // Make Email field readonly
      this.createUserForm.get('email')?.disable();
    }
  }

  /**
   * Parse Thai date format to dd/mm/yyyy (Buddhist Era)
   * Converts formats like "30 April 2567" or "2025-04-30" to "30/04/2568"
   */
  private parseThaiDate(thaiDateStr: string): string | null {
    if (!thaiDateStr || thaiDateStr === 'ตลอดชีพ') {
      return null;
    }

    try {
      // Check if date is in ISO format (YYYY-MM-DD) from backend
      const isoPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
      const isoMatch = thaiDateStr.match(isoPattern);
      
      if (isoMatch) {
        // Convert from AD ISO format to BE dd/mm/yyyy format
        const year = parseInt(isoMatch[1]);
        const month = isoMatch[2];
        const day = isoMatch[3];
        const buddhistYear = year + 543;
        
        const formattedDate = `${day}/${month}/${buddhistYear}`;
        console.log(`📅 Converted ISO date "${thaiDateStr}" to BE format "${formattedDate}"`);
        return formattedDate;
      }

      // Thai month names mapping
      const thaiMonths: { [key: string]: number } = {
        'January': 1, 'มกราคม': 1,
        'February': 2, 'กุมภาพันธ์': 2,
        'March': 3, 'มีนาคม': 3,
        'April': 4, 'เมษายน': 4,
        'May': 5, 'พฤษภาคม': 5,
        'June': 6, 'มิถุนายน': 6,
        'July': 7, 'กรกฎาคม': 7,
        'August': 8, 'สิงหาคม': 8,
        'September': 9, 'กันยายน': 9,
        'October': 10, 'ตุลาคม': 10,
        'November': 11, 'พฤศจิกายน': 11,
        'December': 12, 'ธันวาคม': 12
      };

      // Parse format: "30 April 2567"
      const parts = thaiDateStr.trim().split(/\s+/);
      if (parts.length !== 3) {
        console.warn('⚠️ Unexpected date format:', thaiDateStr);
        return null;
      }

      const day = parseInt(parts[0]);
      const monthName = parts[1];
      const buddhistYear = parseInt(parts[2]);

      // Get month number
      const month = thaiMonths[monthName];
      if (!month) {
        console.warn('⚠️ Unknown month name:', monthName);
        return null;
      }

      // Format as dd/mm/yyyy (Buddhist Era)
      const formattedDate = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${buddhistYear}`;

      console.log(`📅 Converted Thai date "${thaiDateStr}" to BE format "${formattedDate}"`);
      return formattedDate;
    } catch (error) {
      console.error('❌ Error parsing Thai date:', error);
      return null;
    }
  }

  /**
   * Load reference user data from API and auto-fill prefix, name, and lastname
   */
  private loadReferenceUserData(): void {
    if (!this.userCid) {
      console.log('ℹ️ No CID available to fetch reference user data');
      return;
    }

    // Remove spaces from CID for API call
    const cleanCid = this.userCid.replace(/\s+/g, '');

    console.log('🔍 Fetching reference user data for CID:', cleanCid);

    this.http.get<any>(`${environment.apiUrl}/reference-user-data/by-id-card/${cleanCid}`).subscribe({
      next: (data) => {
        console.log('✅ Reference user data found:', data);
        console.log('🔍 Checking expirationDate value:', data.expirationDate);
        console.log('🔍 expirationDate type:', typeof data.expirationDate);
        console.log('🔍 Exact comparison:', data.expirationDate === 'ตลอดชีพ');

        // Auto-fill prefix, firstName, and lastName if data exists
        if (data) {
          const updateData: any = {};

          if (data.title) {
            updateData.prefix = data.title;
            console.log('📝 Auto-filled prefix:', data.title);
          }

          if (data.name) {
            updateData.firstName = data.name;
            console.log('📝 Auto-filled firstName:', data.name);
          }

          if (data.lastname) {
            updateData.lastName = data.lastname;
            console.log('📝 Auto-filled lastName:', data.lastname);
          }

          // Auto-select "สามัญ" for VAT member type if idMember exists
          if (data.idMember) {
            updateData.vatMemberType = 'สามัญ';
            updateData.vatId = data.idMember;
            console.log('📝 Auto-selected VAT member type: สามัญ');
            console.log('📝 Auto-filled VAT ID:', data.idMember);

            // Check if membership is lifetime
            console.log('🔍 Checking lifetime condition...');
            if (data.expirationDate === 'ตลอดชีพ') {
              console.log("check")
              updateData.isLifetimeMember = true;
              console.log('✅ Setting isLifetimeMember to TRUE');
            } else {
              console.log('❌ expirationDate is NOT "ตลอดชีพ", it is:', data.expirationDate);

              // Parse and convert the date to YYYY-MM-DD format
              const parsedDate = this.parseThaiDate(data.expirationDate);
              if (parsedDate) {
                updateData.vatMemberExpiry = parsedDate;
                console.log('📅 Auto-filled expiry date:', parsedDate);
              }
            }
          }

          console.log('📦 Update data object before patching:', updateData);

          // Patch the form with the fetched data FIRST
          this.createUserForm.patchValue(updateData);

          console.log('✅ Form patched. isLifetimeMember value:', this.createUserForm.get('isLifetimeMember')?.value);

          // THEN handle field enabling/disabling after patching
          if (data.idMember) {
            // Always enable VAT ID field
            this.createUserForm.get('vatId')?.enable();

            // Handle expiry date field based on lifetime status
            if (data.expirationDate === 'ตลอดชีพ') {
              // Disable expiry date field for lifetime members
              this.createUserForm.get('vatMemberExpiry')?.disable();
              console.log('🔒 Disabled vatMemberExpiry field');
            } else {
              // Enable VAT member expiry field for non-lifetime members
              this.createUserForm.get('vatMemberExpiry')?.enable();
              console.log('🔓 Enabled vatMemberExpiry field');
            }
          }

          console.log('✅ Successfully auto-filled user data from reference');
        }
      },
      error: (error) => {
        // Don't show error to user, just log it
        // This is expected if user is not in reference database
        console.log('ℹ️ No reference user data found for this CID (this is normal for new users)');
        console.log('Error details:', error.status, error.message);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Set maximum birth date to today
   */
  private setMaxBirthDate(): void {
    const today = new Date();
    this.maxBirthDate = today.toISOString().split('T')[0];
  }

  /**
   * Handle birth date input with auto-formatting (dd/mm/yyyy BE)
   */
  onBirthDateInput(event: any): void {
    let value = event.target.value.replace(/[^0-9]/g, '');
    
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    this.createUserForm.patchValue({ birthDate: value }, { emitEvent: false });
  }

  /**
   * Validate birth date format and convert BE to Gregorian for storage
   */
  validateBirthDate(): void {
    const birthDateValue = this.createUserForm.get('birthDate')?.value;
    
    if (!birthDateValue) {
      return;
    }

    // Check format dd/mm/yyyy
    const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = birthDateValue.match(datePattern);
    
    if (!match) {
      this.createUserForm.get('birthDate')?.setErrors({ invalidFormat: true });
      return;
    }

    const day = parseInt(match[1]);
    const month = parseInt(match[2]);
    const yearBE = parseInt(match[3]);
    
    // Convert BE to Gregorian
    const yearCE = yearBE - 543;
    
    // Validate date ranges
    if (day < 1 || day > 31 || month < 1 || month > 12 || yearBE < 2400 || yearBE > 2569) {
      this.createUserForm.get('birthDate')?.setErrors({ invalidDate: true });
      return;
    }

    // Check if date is valid
    const testDate = new Date(yearCE, month - 1, day);
    if (testDate.getDate() !== day || testDate.getMonth() !== month - 1) {
      this.createUserForm.get('birthDate')?.setErrors({ invalidDate: true });
      return;
    }

    // Check if date is not in the future
    const today = new Date();
    if (testDate > today) {
      this.createUserForm.get('birthDate')?.setErrors({ futureDate: true });
      return;
    }

    // Store the Gregorian date in ISO format for backend (but keep BE display in form)
    this.birthDateBE = birthDateValue; // Keep BE format for display
    
    // Clear any errors
    this.createUserForm.get('birthDate')?.setErrors(null);
  }

  /**
   * Handle VAT member expiry date input with auto-formatting (dd/mm/yyyy BE)
   */
  onVatMemberExpiryInput(event: any): void {
    let value = event.target.value.replace(/[^0-9]/g, '');
    
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    this.createUserForm.patchValue({ vatMemberExpiry: value }, { emitEvent: false });
  }

  /**
   * Validate VAT member expiry date format and convert BE to Gregorian for storage
   */
  validateVatMemberExpiry(): void {
    const expiryDateValue = this.createUserForm.get('vatMemberExpiry')?.value;
    
    if (!expiryDateValue) {
      return;
    }

    // Check format dd/mm/yyyy
    const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = expiryDateValue.match(datePattern);
    
    if (!match) {
      this.createUserForm.get('vatMemberExpiry')?.setErrors({ invalidFormat: true });
      return;
    }

    const day = parseInt(match[1]);
    const month = parseInt(match[2]);
    const yearBE = parseInt(match[3]);
    
    // Convert BE to Gregorian
    const yearCE = yearBE - 543;
    
    // Validate date ranges
    if (day < 1 || day > 31 || month < 1 || month > 12 || yearBE < 2400 || yearBE > 2600) {
      this.createUserForm.get('vatMemberExpiry')?.setErrors({ invalidDate: true });
      return;
    }

    // Check if date is valid
    const testDate = new Date(yearCE, month - 1, day);
    if (testDate.getDate() !== day || testDate.getMonth() !== month - 1) {
      this.createUserForm.get('vatMemberExpiry')?.setErrors({ invalidDate: true });
      return;
    }

    // Store the BE format for display
    this.vatMemberExpiryBE = expiryDateValue;
    
    // Clear any errors
    this.createUserForm.get('vatMemberExpiry')?.setErrors(null);
  }

  /**
   * Handle Valuer rank expiry date input with auto-formatting (dd/mm/yyyy BE)
   */
  onValuerRankExpiryInput(event: any): void {
    let value = event.target.value.replace(/[^0-9]/g, '');
    
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    this.createUserForm.patchValue({ valuerRankExpiry: value }, { emitEvent: false });
  }

  /**
   * Validate Valuer rank expiry date format and convert BE to Gregorian for storage
   */
  validateValuerRankExpiry(): void {
    const expiryDateValue = this.createUserForm.get('valuerRankExpiry')?.value;
    
    if (!expiryDateValue) {
      return;
    }

    // Check format dd/mm/yyyy
    const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = expiryDateValue.match(datePattern);
    
    if (!match) {
      this.createUserForm.get('valuerRankExpiry')?.setErrors({ invalidFormat: true });
      return;
    }

    const day = parseInt(match[1]);
    const month = parseInt(match[2]);
    const yearBE = parseInt(match[3]);
    
    // Convert BE to Gregorian
    const yearCE = yearBE - 543;
    
    // Validate date ranges
    if (day < 1 || day > 31 || month < 1 || month > 12 || yearBE < 2400 || yearBE > 2600) {
      this.createUserForm.get('valuerRankExpiry')?.setErrors({ invalidDate: true });
      return;
    }

    // Check if date is valid
    const testDate = new Date(yearCE, month - 1, day);
    if (testDate.getDate() !== day || testDate.getMonth() !== month - 1) {
      this.createUserForm.get('valuerRankExpiry')?.setErrors({ invalidDate: true });
      return;
    }

    // Store the BE format for display
    this.valuerRankExpiryBE = expiryDateValue;
    
    // Clear any errors
    this.createUserForm.get('valuerRankExpiry')?.setErrors(null);
  }

  /**
   * Convert BE date string (dd/mm/yyyy) to Gregorian ISO format (yyyy-mm-dd)
   */
  private convertBEtoGregorian(beDateStr: string): string | null {
    const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = beDateStr.match(datePattern);
    
    if (!match) return null;
    
    const day = match[1];
    const month = match[2];
    const yearBE = parseInt(match[3]);
    const yearCE = yearBE - 543;
    
    return `${yearCE}-${month}-${day}`;
  }

  /**
   * Initialize the reactive form
   */
  private initForm(): void {
    this.createUserForm = this.fb.group({
      // Step 1: Personal Information
      prefix: [''],
      firstNameThai: [''],
      lastNameThai: [''],
      firstNameEnglish: [''],
      lastNameEnglish: [''],
      firstName: [''],
      lastName: [''],
      email: [''],
      phone: [''],
      phoneNumber: [''],
      cid: [''],
      gender: [''],
      birthDate: [''],
      dateOfBirth: [''],
      maritalStatus: [''],
      nationality: [''],

      // Address Information
      registeredAddress: [''],
      registeredSubdistrict: [''],
      registeredDistrict: [''],
      registeredProvince: [''],
      registeredPostalCode: [''],

      address: [''],
      subdistrict: [''],
      district: [''],
      province: [''],
      postalCode: [''],

      currentAddress: [''],
      currentSubdistrict: [''],
      currentDistrict: [''],
      currentProvince: [''],
      currentPostalCode: [''],
      currentPhone: [''],
      currentFax: [''],
      currentEmail: [''],
      currentLineId: [''],
      currentFacebook: [''],

      sameAsRegistered: [false],

      // Step 2: Professional Information
      memberType: [''],
      education: [''],
      educationField: [''],
      valuationExperienceYears: [''],
      valuationExperienceMonths: [''],

      workplaceName: [''],
      businessType: [''],
      workplaceAddress: [''],
      workplacePhone: [''],
      workplaceFax: [''],
      workplaceEmail: [''],
      workplaceLineId: [''],
      workplaceFacebook: [''],
      organization: [''],
      companyId: [''],

      vatMemberType: ['ยังไม่มีบัตรสมาชิก'],
      vatMemberExpiry: [''],
      vatId: [''],
      vatExpired: [''],
      vatCpd: [0],
      isLifetimeMember: [false],

      valuerRank: ['ไม่มี'],
      valuerRankExpiry: [''],
      valuerId: [''],
      valuerExpired: [''],

      tvaCpd: [0],
      otherCpd: [0],
      totalCpd: [0],

      notes: [''],
      profilePicture: [''],

      // Dynamic arrays
      workHistory: this.fb.array([]),
      valuationExperience: this.fb.array([]),
      educationHistory: this.fb.array([]),
      educationBackground: this.fb.array([]),
      specialCourses: this.fb.array([]),
      vatCourses: this.fb.array([]),

      // Consent
      consentAccepted: [false, Validators.requiredTrue]
    });
  }


  /**
   * Load select data (licenses and ranks)
   * Note: These methods will be implemented when UserService is updated
   */
  private loadSelectData(): void {
    // TODO: Implement when UserService has getLicenseTypes and getRankTypes methods
    this.isLoadingData = false;
  }

  /**
   * Load courses from API
   * Only loads courses with courseStatusId = 3 and endDate before 1 January 2569 (2026-01-01)
   */
  private loadCourses(): void {
    this.isLoadingCourses = true;
    this.http.get<any[]>(`${environment.apiUrl}/courses`).subscribe({
      next: (courses) => {
        // Filter courses to only show those with courseStatusId = 3 and endDate before 2026-01-01
        const cutoffDate = new Date('2026-01-01');
        this.courseList = courses.filter(course => {
          if (course.courseStatusId !== 3) return false;

          // Check if endDate exists and is before cutoff date
          if (course.endDate) {
            const courseEndDate = new Date(course.endDate);
            return courseEndDate < cutoffDate;
          }

          return false; // Exclude courses without endDate
        });
        this.isLoadingCourses = false;
        console.log('📚 Loaded courses (courseStatusId = 3, endDate < 2026-01-01):', this.courseList.length, 'out of', courses.length, 'total');
      },
      error: (error) => {
        console.error('❌ Error loading courses:', error);
        this.isLoadingCourses = false;
      }
    });
  }

  /**
   * Load companies from API
   * Handles both 200 (OK) and 304 (Not Modified) responses
   */
  private loadCompanies(): void {
    this.isLoadingCompanies = true;
    this.http.get<any>(`${environment.apiUrl}/companies?page=1&limit=100`, { 
      observe: 'response' 
    }).subscribe({
      next: (response) => {
        // Handle both 200 and 304 status codes
        if (response.status === 200 || response.status === 304) {
          // For 304, response.body might be null, so we keep existing data
          if (response.body && response.body.data) {
            this.companyList = response.body.data || [];
            this.filteredCompanyList = [...this.companyList];
            console.log('🏢 Loaded companies (status ' + response.status + '):', this.companyList.length);
          } else if (response.status === 304 && this.companyList.length === 0) {
            // If 304 but no cached data in component, try to use empty array
            console.log('⚠️ Received 304 but no cached data available');
            this.companyList = [];
            this.filteredCompanyList = [];
          } else {
            console.log('🏢 Using cached company data (304):', this.companyList.length);
          }
        }
        this.isLoadingCompanies = false;
      },
      error: (error) => {
        console.error('❌ Error loading companies:', error);
        // Even on error, ensure loading state is cleared
        this.isLoadingCompanies = false;
        // Initialize with empty arrays if not already set
        if (!this.companyList) {
          this.companyList = [];
          this.filteredCompanyList = [];
        }
      }
    });
  }

  /**
   * Initialize dynamic arrays with default rows
   */
  private initializeDynamicArrays(): void {
    this.addWorkHistory();
    this.addValuationExperience();
    this.addEducationHistory();
    this.addEducationBackground();
    this.addSpecialCourse();
    this.addVatCourse();
  }

  /**
   * Setup watchers for address fields
   */
  private setupAddressWatchers(): void {
    const registeredFields = ['registeredAddress', 'registeredSubdistrict', 'registeredDistrict', 'registeredProvince', 'registeredPostalCode'];

    registeredFields.forEach(field => {
      this.createUserForm.get(field)?.valueChanges.subscribe(() => {
        if (this.createUserForm.get('sameAsRegistered')?.value) {
          this.copyRegisteredToCurrent();
        }
      });
    });
  }

  /**
   * Setup watchers for professional information fields
   */
  private setupProfessionalWatchers(): void {
    // Watch VAT Member Type
    this.createUserForm.get('vatMemberType')?.valueChanges.subscribe(value => {
      if (value === 'ยังไม่มีบัตรสมาชิก') {
        this.createUserForm.get('vatMemberExpiry')?.setValue('');
        this.createUserForm.get('vatId')?.setValue('');
        this.createUserForm.get('vatMemberExpiry')?.disable();
        this.createUserForm.get('vatId')?.disable();
      } else {
        this.createUserForm.get('vatMemberExpiry')?.enable();
        this.createUserForm.get('vatId')?.enable();
      }
    });

    // Watch Valuer Rank
    this.createUserForm.get('valuerRank')?.valueChanges.subscribe(value => {
      if (value === 'ไม่มี') {
        this.createUserForm.get('valuerRankExpiry')?.setValue('');
        this.createUserForm.get('valuerId')?.setValue('');
        this.createUserForm.get('valuerRankExpiry')?.disable();
        this.createUserForm.get('valuerId')?.disable();
      } else {
        this.createUserForm.get('valuerRankExpiry')?.enable();
        this.createUserForm.get('valuerId')?.enable();
      }
    });
  }

  /**
   * Handle same as registered checkbox change
   */
  onSameAsRegisteredChange(event: any): void {
    const isChecked = event.target.checked;

    if (isChecked) {
      this.copyRegisteredToCurrent();
      this.toggleCurrentAddressFields(false);
    } else {
      this.toggleCurrentAddressFields(true);
    }
  }

  /**
   * Handle lifetime member checkbox change
   */
  onLifetimeMemberChange(event: any): void {
    const isChecked = event.target.checked;

    if (isChecked) {
      // Disable and clear the expiry date field
      this.createUserForm.get('vatMemberExpiry')?.setValue('');
      this.createUserForm.get('vatMemberExpiry')?.disable();
      console.log('📝 Lifetime member checked - disabled expiry date field');
    } else {
      // Enable the expiry date field
      this.createUserForm.get('vatMemberExpiry')?.enable();
      console.log('📝 Lifetime member unchecked - enabled expiry date field');
    }
  }

  /**
   * Copy registered address to current address
   */
  private copyRegisteredToCurrent(): void {
    const registeredData = {
      currentAddress: this.createUserForm.get('registeredAddress')?.value,
      currentProvince: this.createUserForm.get('registeredProvince')?.value,
      currentDistrict: this.createUserForm.get('registeredDistrict')?.value,
      currentSubdistrict: this.createUserForm.get('registeredSubdistrict')?.value,
      currentPostalCode: this.createUserForm.get('registeredPostalCode')?.value
    };

    // Copy dropdown lists
    this.currentDistrictList = [...this.registeredDistrictList];
    this.currentSubdistrictList = [...this.registeredSubdistrictList];
    this.currentZipCodeList = [...this.registeredZipCodeList];

    this.createUserForm.patchValue(registeredData, { emitEvent: false });
  }

  /**
   * Toggle current address fields enabled/disabled
   */
  private toggleCurrentAddressFields(enabled: boolean): void {
    const currentFields = ['currentAddress', 'currentSubdistrict', 'currentDistrict', 'currentProvince', 'currentPostalCode'];

    currentFields.forEach(field => {
      const control = this.createUserForm.get(field);
      if (enabled) {
        control?.enable();
      } else {
        control?.disable();
      }
    });
  }

  // Valuation Experience Methods
  get valuationExperience(): FormArray {
    return this.createUserForm.get('valuationExperience') as FormArray;
  }

  addValuationExperience(): void {
    const experienceGroup = this.fb.group({
      organization: [''],
      position: [''],
      years: ['']
    });

    this.valuationExperience.push(experienceGroup);
  }

  removeValuationExperience(index: number): void {
    if (this.valuationExperience.length > 1) {
      this.valuationExperience.removeAt(index);
    }
  }

  // Work History Methods
  get workHistory(): FormArray {
    return this.createUserForm.get('workHistory') as FormArray;
  }

  addWorkHistory(): void {
    const workGroup = this.fb.group({
      Organization: [''],
      Position: [''],
      Years: ['']
    });

    this.workHistory.push(workGroup);
  }

  removeWorkHistory(index: number): void {
    if (this.workHistory.length > 1) {
      this.workHistory.removeAt(index);
    }
  }

  // Education History Methods
  get educationHistory(): FormArray {
    return this.createUserForm.get('educationHistory') as FormArray;
  }

  addEducationHistory(): void {
    const educationGroup = this.fb.group({
      Institution: [''],
      'Degree and Major': [''],
      'Year Complete': ['']
    });

    this.educationHistory.push(educationGroup);
  }

  removeEducationHistory(index: number): void {
    if (this.educationHistory.length > 1) {
      this.educationHistory.removeAt(index);
    }
  }

  // Education Background Methods
  get educationBackground(): FormArray {
    return this.createUserForm.get('educationBackground') as FormArray;
  }

  addEducationBackground(): void {
    const educationGroup = this.fb.group({
      institution: [''],
      degree: [''],
      year: ['']
    });

    this.educationBackground.push(educationGroup);
  }

  removeEducationBackground(index: number): void {
    if (this.educationBackground.length > 1) {
      this.educationBackground.removeAt(index);
    }
  }

  // Special Courses Methods
  get specialCourses(): FormArray {
    return this.createUserForm.get('specialCourses') as FormArray;
  }

  addSpecialCourse(): void {
    const courseGroup = this.fb.group({
      courseTopic: [''],
      organization: [''],
      cpdHours: [''],
      trainingDate: [''],
      status: ['']
    });

    this.specialCourses.push(courseGroup);
  }

  removeSpecialCourse(index: number): void {
    if (this.specialCourses.length > 1) {
      this.specialCourses.removeAt(index);
    }
  }

  // VAT Courses Methods
  get vatCourses(): FormArray {
    return this.createUserForm.get('vatCourses') as FormArray;
  }

  addVatCourse(): void {
    const courseGroup = this.fb.group({
      courseId: [''],
      courseName: [''],
      cpdHours: [''],
      completionDate: [''],
      recordId: [''],
      evidenceFile: [null],
      evidenceFileName: [''],
      uploadStatus: [''], // 'uploading', 'success', 'error'
      imageThumbnail: [''] // URL to the uploaded image
    });

    this.vatCourses.push(courseGroup);
  }

  removeVatCourse(index: number): void {
    if (this.vatCourses.length > 1) {
      this.vatCourses.removeAt(index);
    }
  }

  /**
   * View VAT course evidence image in new tab
   */
  viewVatCourseImage(index: number): void {
    const courseGroup = this.vatCourses.at(index) as FormGroup;
    const imageThumbnail = courseGroup.get('imageThumbnail')?.value;

    if (imageThumbnail) {
      window.open(imageThumbnail, '_blank');
    } else {
      alert('ไม่พบรูปภาพ');
    }
  }

  /**
   * Handle file selection and automatically upload for VAT course evidence
   */
  onVatCourseFileSelectAndUpload(index: number, event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('กรุณาเลือกไฟล์รูปภาพหรือ PDF เท่านั้น (JPG, PNG, GIF, PDF)');
      event.target.value = '';
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
      event.target.value = '';
      return;
    }

    const courseGroup = this.vatCourses.at(index) as FormGroup;

    // Store file in form control
    courseGroup.patchValue({
      evidenceFile: file,
      evidenceFileName: file.name
    });

    console.log('📎 File selected, starting automatic upload:', file.name);

    // Automatically upload the file
    this.uploadEvidenceFile(index, file);
  }

  /**
   * Handle profile picture selection and upload
   */
  onProfilePictureSelect(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('กรุณาเลือกไฟล์รูปภาพเท่านั้น (JPG, PNG, GIF)');
      event.target.value = '';
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
      event.target.value = '';
      return;
    }

    this.isUploadingProfile = true;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'profiles');

    const apiUrl = `${environment.apiUrl}/upload/image`;

    console.log('📤 Uploading profile image:', file.name);

    this.http.post(apiUrl, formData).subscribe({
      next: (response: any) => {
        console.log('✅ Profile image uploaded successfully:', response);
        this.isUploadingProfile = false;

        // Set returned URL to form control
        if (response && response.url) {
          this.createUserForm.patchValue({ profilePicture: response.url });
        } else if (response && response.data && response.data.url) {
          this.createUserForm.patchValue({ profilePicture: response.data.url });
        }
      },
      error: (error) => {
        console.error('❌ Error uploading profile image:', error);
        this.isUploadingProfile = false;
        alert('เกิดข้อผิดพลาดในการอัพโหลดรูปโปรไฟล์: ' + (error.error?.message || error.message));
        event.target.value = '';
      }
    });
  }

  private uploadEvidenceFile(index: number, file: File): void {
    const courseGroup = this.vatCourses.at(index) as FormGroup;

    // Set uploading status
    courseGroup.patchValue({ uploadStatus: 'uploading' });

    // Prepare FormData for file upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'cpd-evidence');

    const apiUrl = `${environment.apiUrl}/upload/image`;

    console.log('📤 Uploading evidence file');
    console.log('📍 API URL:', apiUrl);
    console.log('📎 File name:', file.name);

    // POST file to upload-image endpoint
    this.http.post(apiUrl, formData).subscribe({
      next: (response: any) => {
        console.log('✅ Evidence uploaded successfully:', response);

        // Set success status and update imageThumbnail
        const updateData: any = { uploadStatus: 'success' };

        // Handle response format from /upload/image might be response.url or response.data.url
        if (response && response.url) {
          updateData.imageThumbnail = response.url;
        } else if (response && response.data && response.data.url) {
          updateData.imageThumbnail = response.data.url;
        }

        courseGroup.patchValue(updateData);

        // Clear file input and success status after 3 seconds
        setTimeout(() => {
          const fileInput = document.getElementById(`vatCourseFile_${index}`) as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }
          courseGroup.patchValue({ uploadStatus: '' });
        }, 3000);
      },
      error: (error) => {
        console.error('❌ Error uploading evidence:', error);

        // Set error status
        courseGroup.patchValue({ uploadStatus: 'error' });

        let errorMessage = 'เกิดข้อผิดพลาดในการอัพโหลดไฟล์';
        if (error.error?.message) {
          errorMessage = error.error.message;
        }

        alert(`เกิดข้อผิดพลาด: ${errorMessage}`);

        // Clear error status after 3 seconds
        setTimeout(() => {
          courseGroup.patchValue({ uploadStatus: '' });
        }, 3000);
      }
    });
  }



  /**
   * Handle course selection change
   */
  onCourseSelect(index: number, event: any): void {
    const courseId = event.target.value;

    // Check if this course is already selected in another row
    const isDuplicate = this.vatCourses.controls.some((control, i) => {
      if (i === index) return false; // Skip current row
      const existingCourseId = (control as FormGroup).get('courseId')?.value;
      return existingCourseId === courseId && courseId !== '';
    });

    if (isDuplicate) {
      alert('ไม่สามารถเลือกคอร์สซ้ำได้ กรุณาเลือกคอร์สอื่น');
      // Reset the dropdown to empty
      event.target.value = '';
      const courseGroup = this.vatCourses.at(index) as FormGroup;
      courseGroup.patchValue({
        courseId: '',
        courseName: '',
        cpdHours: '',
        completionDate: ''
      });
      return;
    }

    const selectedCourse = this.courseList.find(c => c.id === courseId);

    if (selectedCourse) {
      const courseGroup = this.vatCourses.at(index) as FormGroup;

      // Format endDate to YYYY-MM-DD for date input
      let formattedEndDate = '';
      if (selectedCourse.endDate) {
        const endDate = new Date(selectedCourse.endDate);
        if (!isNaN(endDate.getTime())) {
          formattedEndDate = endDate.toISOString().split('T')[0];
        }
      }

      courseGroup.patchValue({
        courseId: selectedCourse.id,
        courseName: selectedCourse.title,
        cpdHours: parseFloat(selectedCourse.duration) || 0,
        completionDate: formattedEndDate
      });
    }
  }

  /**
   * Handle company selection change
   */
  onCompanySelect(event: any): void {
    const companyId = event.target.value;
    if (companyId) {
      this.selectedCompany = this.companyList.find(c => c.id === companyId);
      console.log('🏢 Selected company:', this.selectedCompany);
    } else {
      this.selectedCompany = null;
    }
  }

  /**
   * Filter company list based on search text
   */
  onCompanySearchChange(): void {
    const searchText = this.companySearchText.toLowerCase().trim();
    
    if (!searchText) {
      this.filteredCompanyList = [...this.companyList];
    } else {
      this.filteredCompanyList = this.companyList.filter(company => 
        company.organizationName?.toLowerCase().includes(searchText)
      );
    }
    
    console.log('🔍 Filtered companies:', this.filteredCompanyList.length);
  }

  /**
   * Select company from filtered list
   */
  selectCompany(company: any): void {
    this.selectedCompany = company;
    this.companySearchText = company.organizationName;
    this.createUserForm.patchValue({ companyId: company.id });
    this.showCompanyDropdown = false;
    console.log('🏢 Selected company:', this.selectedCompany);
  }

  /**
   * Show company dropdown
   */
  onCompanyInputFocus(): void {
    this.showCompanyDropdown = true;
    this.onCompanySearchChange();
  }

  /**
   * Clear company selection
   */
  clearCompanySelection(): void {
    this.selectedCompany = null;
    this.companySearchText = '';
    this.createUserForm.patchValue({ companyId: '' });
    this.filteredCompanyList = [...this.companyList];
  }

  // ==================== Registered Address Cascading Dropdowns ====================

  /**
   * Handle registered province selection
   */
  onRegisteredProvinceChange(event: any): void {
    const provinceName = event.target.value;
    
    // Reset dependent fields
    this.createUserForm.patchValue({
      registeredDistrict: '',
      registeredSubdistrict: '',
      registeredPostalCode: ''
    });
    
    this.registeredDistrictList = [];
    this.registeredSubdistrictList = [];
    this.registeredZipCodeList = [];
    
    if (provinceName) {
      const selectedProvince = this.provinceList.find(p => p.name_th === provinceName);
      if (selectedProvince && selectedProvince.districts) {
        this.registeredDistrictList = selectedProvince.districts;
      }
    }
    
    // Copy to current address if checkbox is checked
    if (this.createUserForm.get('sameAsRegistered')?.value) {
      this.copyRegisteredToCurrent();
    }
  }

  /**
   * Handle registered district selection
   */
  onRegisteredDistrictChange(event: any): void {
    const districtName = event.target.value;
    
    // Reset dependent fields
    this.createUserForm.patchValue({
      registeredSubdistrict: '',
      registeredPostalCode: ''
    });
    
    this.registeredSubdistrictList = [];
    this.registeredZipCodeList = [];
    
    if (districtName) {
      const selectedDistrict = this.registeredDistrictList.find(d => d.name_th === districtName);
      if (selectedDistrict && selectedDistrict.sub_districts) {
        this.registeredSubdistrictList = selectedDistrict.sub_districts;
      }
    }
    
    // Copy to current address if checkbox is checked
    if (this.createUserForm.get('sameAsRegistered')?.value) {
      this.copyRegisteredToCurrent();
    }
  }

  /**
   * Handle registered subdistrict selection
   */
  onRegisteredSubdistrictChange(event: any): void {
    const subdistrictName = event.target.value;
    
    // Reset postal code
    this.createUserForm.patchValue({
      registeredPostalCode: ''
    });
    
    this.registeredZipCodeList = [];
    
    if (subdistrictName) {
      const selectedSubdistrict = this.registeredSubdistrictList.find(s => s.name_th === subdistrictName);
      if (selectedSubdistrict) {
        // Get unique zip codes for this subdistrict
        const zipCodes = [selectedSubdistrict.zip_code];
        this.registeredZipCodeList = [...new Set(zipCodes)].map(zip => ({ code: zip }));
        
        // Auto-select if only one zip code
        if (this.registeredZipCodeList.length === 1) {
          this.createUserForm.patchValue({
            registeredPostalCode: this.registeredZipCodeList[0].code.toString()
          });
        }
      }
    }
    
    // Copy to current address if checkbox is checked
    if (this.createUserForm.get('sameAsRegistered')?.value) {
      this.copyRegisteredToCurrent();
    }
  }

  /**
   * Handle registered postal code selection
   */
  onRegisteredPostalCodeChange(event: any): void {
    // Copy to current address if checkbox is checked
    if (this.createUserForm.get('sameAsRegistered')?.value) {
      this.copyRegisteredToCurrent();
    }
  }

  // ==================== Current Address Cascading Dropdowns ====================

  /**
   * Handle current province selection
   */
  onCurrentProvinceChange(event: any): void {
    const provinceName = event.target.value;
    
    // Reset dependent fields
    this.createUserForm.patchValue({
      currentDistrict: '',
      currentSubdistrict: '',
      currentPostalCode: ''
    });
    
    this.currentDistrictList = [];
    this.currentSubdistrictList = [];
    this.currentZipCodeList = [];
    
    if (provinceName) {
      const selectedProvince = this.provinceList.find(p => p.name_th === provinceName);
      if (selectedProvince && selectedProvince.districts) {
        this.currentDistrictList = selectedProvince.districts;
      }
    }
  }

  /**
   * Handle current district selection
   */
  onCurrentDistrictChange(event: any): void {
    const districtName = event.target.value;
    
    // Reset dependent fields
    this.createUserForm.patchValue({
      currentSubdistrict: '',
      currentPostalCode: ''
    });
    
    this.currentSubdistrictList = [];
    this.currentZipCodeList = [];
    
    if (districtName) {
      const selectedDistrict = this.currentDistrictList.find(d => d.name_th === districtName);
      if (selectedDistrict && selectedDistrict.sub_districts) {
        this.currentSubdistrictList = selectedDistrict.sub_districts;
      }
    }
  }

  /**
   * Handle current subdistrict selection
   */
  onCurrentSubdistrictChange(event: any): void {
    const subdistrictName = event.target.value;
    
    // Reset postal code
    this.createUserForm.patchValue({
      currentPostalCode: ''
    });
    
    this.currentZipCodeList = [];
    
    if (subdistrictName) {
      const selectedSubdistrict = this.currentSubdistrictList.find(s => s.name_th === subdistrictName);
      if (selectedSubdistrict) {
        // Get unique zip codes for this subdistrict
        const zipCodes = [selectedSubdistrict.zip_code];
        this.currentZipCodeList = [...new Set(zipCodes)].map(zip => ({ code: zip }));
        
        // Auto-select if only one zip code
        if (this.currentZipCodeList.length === 1) {
          this.createUserForm.patchValue({
            currentPostalCode: this.currentZipCodeList[0].code.toString()
          });
        }
      }
    }
  }

  /**
   * Navigate to next step
   */
  nextStep(): void {
    if (this.validateCurrentStep()) {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      }
    }
  }

  /**
   * Navigate to previous step
   */
  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  /**
   * Go to specific step
   */
  goToStep(step: number): void {
    if (step <= this.currentStep || this.validateStepsUpTo(step - 1)) {
      this.currentStep = step;
    }
  }

  /**
   * Validate current step fields
   */
  private validateCurrentStep(): boolean {
    let fieldsToValidate: string[] = [];

    switch (this.currentStep) {
      case 1:
        fieldsToValidate = [
          'prefix', 'firstName', 'lastName', 'email', 'phone', 'cid', 'gender', 'birthDate',
          'registeredAddress', 'registeredSubdistrict', 'registeredDistrict', 'registeredProvince', 'registeredPostalCode'
        ];

        if (!this.createUserForm.get('sameAsRegistered')?.value) {
          fieldsToValidate.push('currentAddress', 'currentSubdistrict', 'currentDistrict', 'currentProvince', 'currentPostalCode');
        }
        break;
      case 2:
        fieldsToValidate = ['vatMemberType', 'vatMemberExpiry', 'valuerRank', 'valuerRankExpiry', 'consentAccepted'];
        break;
    }

    let isValid = true;
    fieldsToValidate.forEach(field => {
      const control = this.createUserForm.get(field);
      if (control) {
        control.markAsTouched();
        if (control.invalid) {
          isValid = false;
        }
      }
    });

    // Validate dynamic arrays for step 2
    if (this.currentStep === 2) {
      this.valuationExperience.controls.forEach(control => {
        Object.keys((control as FormGroup).controls).forEach(key => {
          (control as FormGroup).get(key)?.markAsTouched();
        });
      });

      this.educationBackground.controls.forEach(control => {
        Object.keys((control as FormGroup).controls).forEach(key => {
          (control as FormGroup).get(key)?.markAsTouched();
        });
      });
    }

    return isValid;
  }

  /**
   * Validate all steps up to a specific step
   */
  private validateStepsUpTo(step: number): boolean {
    for (let i = 1; i <= step; i++) {
      const currentStepBackup = this.currentStep;
      this.currentStep = i;
      if (!this.validateCurrentStep()) {
        this.currentStep = currentStepBackup;
        return false;
      }
      this.currentStep = currentStepBackup;
    }
    return true;
  }

  /**
   * Check if field has error
   */
  hasError(fieldName: string): boolean {
    const field = this.createUserForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  /**
   * Get error message for field
   */
  getErrorMessage(fieldName: string): string {
    const field = this.createUserForm.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) return 'กรุณากรอกข้อมูล';
    if (field.errors['email']) return 'รูปแบบอีเมลไม่ถูกต้อง';
    if (field.errors['pattern']) {
      if (fieldName === 'phone') return 'เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลัก';
      if (fieldName === 'cid') return 'รหัสบัตรประชาชนต้องเป็นตัวเลข 13 หลัก';
      if (fieldName.includes('PostalCode')) return 'รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก';
    }
    if (field.errors['minlength']) return `ต้องมีอย่างน้อย ${field.errors['minlength'].requiredLength} ตัวอักษร`;
    if (field.errors['min']) return `ค่าต้องมากกว่าหรือเท่ากับ ${field.errors['min'].min}`;
    if (field.errors['max']) return `ค่าต้องน้อยกว่าหรือเท่ากับ ${field.errors['max'].max}`;

    return 'ข้อมูลไม่ถูกต้อง';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Get full address string
   */
  getFullAddress(type: 'registered' | 'current'): string {
    const prefix = type === 'registered' ? 'registered' : 'current';
    const address = this.createUserForm.get(`${prefix}Address`)?.value || '';
    const subdistrict = this.createUserForm.get(`${prefix}Subdistrict`)?.value || '';
    const district = this.createUserForm.get(`${prefix}District`)?.value || '';
    const province = this.createUserForm.get(`${prefix}Province`)?.value || '';
    const postalCode = this.createUserForm.get(`${prefix}PostalCode`)?.value || '';

    if (!address && !subdistrict && !district && !province && !postalCode) {
      return '-';
    }

    return `${address} ต.${subdistrict} อ.${district} จ.${province} ${postalCode}`;
  }

  /**
   * Convert date string to ISO 8601 format
   * Handles both Buddhist Era (dd/mm/yyyy) and Gregorian (YYYY-MM-DD) formats
   */
  private convertToISO8601(dateString: string): string {
    if (!dateString) return '';

    // If already in ISO format, return as is
    if (dateString.includes('T')) return dateString;

    // Check if it's Buddhist Era format (dd/mm/yyyy)
    if (dateString.includes('/')) {
      const gregorianDate = this.convertBEtoGregorian(dateString);
      if (!gregorianDate) return '';
      dateString = gregorianDate;
    }

    // Convert YYYY-MM-DD to ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ)
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    return date.toISOString();
  }

  /**
   * Submit CPD hours data to API as array
   */
  private submitCpdHours(vatCourses: any[]): void {
    if (!vatCourses || vatCourses.length === 0) {
      console.log('ℹ️ No VAT courses to submit');
      return;
    }

    // Filter out empty courses (no courseId selected)
    const validCourses = vatCourses.filter(course => course.courseId && course.cpdHours);

    if (validCourses.length === 0) {
      console.log('ℹ️ No valid VAT courses to submit');
      return;
    }

    console.log('📤 Submitting CPD hours for', validCourses.length, 'courses');

    // Prepare array of CPD hour records
    const cpdRecords = validCourses.map((course) => {
      return {
        userId: this.userId,
        courseId: course.courseId,
        hoursStudied: parseFloat(course.cpdHours) || 0,
        studyDate: course.completionDate || new Date().toISOString().split('T')[0],
        year: course.completionDate ? new Date(course.completionDate).getFullYear() : new Date().getFullYear(),
        sessionTitle: course.courseName || '',
        notes: `Course completed: ${course.courseName}`,
        isVerified: false, // Default to false, will be verified by admin
        imageThumbnail: course.imageThumbnail || ''
      };
    });

    console.log('📤 Submitting CPD hour records array:', cpdRecords);

    // Submit all CPD hour records as a single array in one POST request
    this.http.post(`${environment.apiUrl}/cpd-hours`, cpdRecords).subscribe({
      next: (response) => {
        console.log(`✅ All ${cpdRecords.length} CPD hour records created successfully:`, response);
      },
      error: (error) => {
        console.error('❌ Error creating CPD hour records:', error);
        console.error('Error details:', error.error);
        // Don't show alert for CPD errors to avoid blocking the main flow
      }
    });
  }

  /**
   * Show confirmation modal before submitting
   */
  onSubmit(): void {
    if (!this.userId) {
      alert('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
      this.router.navigate(['/login']);
      return;
    }

    // Show confirmation modal
    this.showConfirmationModal();
  }

  /**
   * Show confirmation modal
   */
  showConfirmationModal(): void {
    const modalRef = this.modalService.open(RegistrationConfirmModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg'
    });

    modalRef.result.then(
      (result) => {
        if (result === 'confirm') {
          this.submitRegistration();
        }
      },
      () => {
        // Modal dismissed (cancelled)
      }
    );
  }

  /**
   * Submit the registration form
   */
  private submitRegistration(): void {
    this.isSubmitting = true;

    const formData = this.createUserForm.getRawValue();

    // Convert dates to ISO 8601 format
    const dateOfBirth = this.convertToISO8601(formData.dateOfBirth || formData.birthDate || '');
    const vatExpired = this.convertToISO8601(formData.vatExpired || formData.vatMemberExpiry || '');
    const valuerExpired = this.convertToISO8601(formData.valuerExpired || formData.valuerRankExpiry || '');

    // Prepare data for API according to the required format
    // Use current form values for prefix, firstName, lastName (including user modifications)
    const currentPrefix = formData.prefix || '';
    const currentFirstName = formData.firstName || '';
    const currentLastName = formData.lastName || '';

    const userInformationData: any = {
      userId: this.userId,
      titleName: currentPrefix,
      firstNameThai: currentFirstName,
      lastNameThai: currentLastName,
      firstNameEnglish: formData.firstNameEnglish || '',
      lastNameEnglish: formData.lastNameEnglish || '',
      memberType: formData.memberType || '',
      education: formData.education || '',
      educationField: formData.educationField || '',
      valuationExperienceYears: formData.valuationExperienceYears || '0',
      valuationExperienceMonths: formData.valuationExperienceMonths || '0',
      maritalStatus: formData.maritalStatus || '',
      nationality: formData.nationality || '',
      workplaceName: formData.workplaceName || '',
      businessType: formData.businessType || '',
      workplaceAddress: formData.workplaceAddress || '',
      workplacePhone: formData.workplacePhone || '',
      workplaceFax: formData.workplaceFax || '',
      workplaceEmail: formData.workplaceEmail || '',
      workplaceLineId: formData.workplaceLineId || '',
      workplaceFacebook: formData.workplaceFacebook || '',
      currentAddress: formData.currentAddress || '',
      currentSubdistrict: formData.currentSubdistrict || '',
      currentDistrict: formData.currentDistrict || '',
      currentProvince: formData.currentProvince || '',
      currentPostalCode: formData.currentPostalCode || '',
      currentPhone: formData.currentPhone || formData.phone || '',
      currentFax: formData.currentFax || '',
      currentEmail: formData.currentEmail || formData.email || '',
      currentLineId: formData.currentLineId || '',
      currentFacebook: formData.currentFacebook || '',
      workHistory: formData.workHistory || [],
      valuationExperience: formData.valuationExperience || [],
      educationHistory: (formData.educationBackground || []).map((edu: any) => ({
        'Institution': edu.institution || '',
        'Degree and Major': edu.degree || '',
        'Year Complete': edu.year || ''
      })),
      specialCourses: formData.specialCourses || [],
      notes: formData.notes || '',
      firstName: currentFirstName,
      lastName: currentLastName,
      phoneNumber: formData.phoneNumber || formData.phone || '',
      address: formData.address || formData.registeredAddress || '',
      subdistrict: formData.subdistrict || formData.registeredSubdistrict || '',
      district: formData.district || formData.registeredDistrict || '',
      province: formData.province || formData.registeredProvince || '',
      postalCode: formData.postalCode || formData.registeredPostalCode || '',
      gender: formData.gender || '',
      profilePicture: formData.profilePicture || '',
      organization: formData.organization || formData.workplaceName || '',
      companyId: formData.companyId || '',
      vatId: formData.vatId || '',
      valuerId: formData.valuerId || '',
      vatType: formData.vatMemberType || '',
      valuerType: formData.valuerRank || '',
      vatCpd: parseFloat(formData.vatCpd) || 0,
      tvaCpd: parseFloat(formData.tvaCpd) || 0,
      otherCpd: parseFloat(formData.otherCpd) || 0,
      totalCpd: parseFloat(formData.totalCpd) || 0
    };

    // Only include date fields if they have valid values
    if (dateOfBirth) {
      userInformationData.dateOfBirth = dateOfBirth;
    }
    if (vatExpired) {
      userInformationData.vatExpired = vatExpired;
    }
    if (valuerExpired) {
      userInformationData.valuerExpired = valuerExpired;
    }

    console.log('📤 Submitting user information:', userInformationData);

    // Call API to create user information
    this.http.post(`${environment.apiUrl}/user-information`, userInformationData).subscribe({
      next: (response) => {
        console.log('✅ User information created successfully:', response);

        // Submit CPD hours data after user information is created
        this.submitCpdHours(formData.vatCourses);

        this.isSubmitting = false;
        // Show success message and redirect
        alert('ลงทะเบียนสำเร็จ คุณจะได้รับแจ้งสถานะการลงทะเบียนทางอีเมล์');
        this.router.navigate(['/user/dashboard']);
      },
      error: (error) => {
        console.error('❌ Error creating user information:', error);
        this.isSubmitting = false;

        let errorMessage = 'เกิดข้อผิดพลาดในการบันทึกข้อมูล';
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.status === 0) {
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
        } else if (error.status === 500) {
          errorMessage = 'เกิดข้อผิดพลาดในเซิร์ฟเวอร์';
        }

        alert(`เกิดข้อผิดพลาด: ${errorMessage}`);
      }
    });
  }

  /**
   * Cancel and go back to login
   */
  cancel(): void {
    if (confirm('คุณต้องการยกเลิกการลงทะเบียนหรือไม่?')) {
      // Clear localStorage
      localStorage.clear();
      console.log('🗑️ LocalStorage cleared on cancel');
      
      // Use window.location for hard redirect to ensure navigation works
      window.location.href = '/login';
    }
  }

  /**
   * Go to login page and clear localStorage
   */
  goToLogin(): void {
    // Clear localStorage
    localStorage.clear();
    console.log('🗑️ LocalStorage cleared on login link click');
    
    // Use window.location for hard redirect to ensure navigation works
    window.location.href = '/login';
  }
}
